/**
 * GuestGuard — protects guest-only routes (sign-in, register).
 *
 * Redirects authenticated users away from guest pages.
 * Defaults to the home page, but the redirect destination
 * can be customized via the `redirectTo` prop.
 *
 * During a password-recovery session, the user is technically
 * authenticated (Supabase keeps the recovery session active)
 * but must NOT be redirected away from the reset page.
 */

import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/auth';
import { ROUTES } from '@/app/routes/paths';
import type { GuardProps } from './types';

export interface GuestGuardProps extends GuardProps {
  redirectTo?: string;
}

export function GuestGuard({ children, redirectTo = ROUTES.HOME }: GuestGuardProps): JSX.Element {
  const { state, passwordRecovery } = useAuth();

  if (state === 'authenticated' && !passwordRecovery) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

GuestGuard.displayName = 'GuestGuard';
